/**
 * Provider registry for the social lookup router (plan 2026-10-31, §4 and §9).
 *
 * A provider is a data record: id, platform, capabilities, family (shared fate), transport,
 * parser, requiresToken. The router (./hedge.ts) picks among the registered providers by health
 * score; this file only holds the types, the registration lists and the env switches.
 *
 * 🔴 Browser+edge-safe: no node:* imports, no dependencies. Every env read below is a STATIC
 *    `process.env.NAME` literal — Next inlines only those at the edge; a dynamic
 *    `process.env[name]` reads undefined there (same rule as the search route).
 *
 * Switches (all optional; zero env set = router ON, every provider ON):
 *   SOCIAL_ROUTER_ENABLED            'false' | '0' | 'off' → router off, today's rotation runs. Unset: ON for
 *                                    APP_NAME up + sibling app, OFF for every other. 'true' forces it on.
 *   SOCIAL_PROV_<ID>_ENABLED         per-provider override ('false' off, 'true' on).
 *   SOCIAL_PROV_<ID>_EMAIL/_PASSWORD credentials of a token (Bearer) provider. A provider that
 *                                    requires a token and has no credentials is simply
 *                                    unavailable (not a failure, never penalised).
 */

export type Platform = 'instagram' | 'tiktok';
export type Capability = 'search_users' | 'profile' | 'exists';
/** Shared fate: estate-a = one backend platform behind several brands, own = instagram.com /
 *  tiktok.com web JSON, rapidapi = RapidAPI-hosted lanes (one pooled key / quota). */
export type Family = 'estate-a' | 'own' | 'rapidapi';
export type Transport = 'bearer' | 'anonymous' | 'browser' | 'rapidapi';

export interface ProviderRequest {
	capability: Capability;
	/** Set by the TikTok entry points so validateResult checks the TikTok profile shape; unset = Instagram. */
	platform?: Platform;
	/** Search prefix (search_users) or exact handle (profile / exists). */
	query: string;
	limit: number;
	/** StarAPI gateway tier of the caller (budget breaker); default 'P3'. */
	tier?: 'P0' | 'P1' | 'P2' | 'P3' | 'P4';
	/** Set by the executor for requiresToken providers. */
	token?: string;
}

/** What `request` hands the generic executor: a plain fetch description. */
export interface BuiltRequest {
	url: string;
	init: { method?: string; headers?: Record<string, string>; body?: string };
}

export interface TokenAuthConfig {
	loginUrl: string;
	loginBody: (email: string, password: string) => { headers?: Record<string, string>; body: string };
	/** Picks the tokens out of the login / refresh response. Throw or return null = failed. */
	parseTokens: (json: any) => { accessToken: string; refreshToken?: string; expiresInS?: number } | null;
	/** Optional refresh_token grant; a failure falls back to a full re-login. */
	refresh?: {
		url: string;
		body: (refreshToken: string) => { headers?: Record<string, string>; body: string };
	};
	/** Token lifetime when neither the JWT `exp` nor `expires_in` says (seconds). */
	fallbackTtlS: number;
}

export interface Provider {
	id: string;
	platform: Platform;
	capabilities: Capability[];
	family: Family;
	transport: Transport;
	requiresToken: boolean;
	/** EWMA seed (ms), from the 2026-10-31 bench. */
	seedLatencyMs: number;
	/** Only tried after every non-last-resort provider failed (StarAPI). */
	lastResort?: boolean;
	/** Default of SOCIAL_PROV_<ID>_ENABLED when unset (default true). */
	defaultEnabled?: boolean;
	/** HTTP statuses this provider uses to say "no such account" (an authoritative miss, not a failure). */
	missStatuses?: number[];
	tokenAuth?: TokenAuthConfig;
	/** Generic transport: describe the HTTP request. Unused when `execute` is set. */
	request?: (req: ProviderRequest) => BuiltRequest;
	/** Turns the response body (parsed JSON, or text when not JSON) into the normalised result.
	 *  Throw ProviderMiss for an authoritative absence, anything else = failure. */
	parser: (body: unknown, req: ProviderRequest) => unknown;
	/** Custom transport (class-based RapidAPI lanes). Must honour `signal` or be raced by the caller. */
	execute?: (req: ProviderRequest, signal: AbortSignal) => Promise<unknown>;
}

/** Authoritative "does not exist" answer. Not a health failure. */
export class ProviderMiss extends Error {
	constructor(message = 'not found') {
		super(message);
		this.name = 'ProviderMiss';
	}
}

/** The provider chose not to run (budget shed, no credentials). Not a health failure. */
export class ProviderSkip extends Error {
	constructor(message = 'skipped') {
		super(message);
		this.name = 'ProviderSkip';
	}
}

/** Upstash retries a failing request with backoff for seconds; the router's Redis calls are best-effort
 *  and must never hold a lookup that long. Rejects after `ms` (the caller treats it as "Redis said nothing"). */
export const REDIS_BOUND_MS = 400;
export function bounded<T>(promise: PromiseLike<T>, ms = REDIS_BOUND_MS): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const timer = setTimeout(() => reject(new Error(`redis call exceeded ${ms} ms`)), ms);
		promise.then(
			(v) => { clearTimeout(timer); resolve(v); },
			(e) => { clearTimeout(timer); reject(e); }
		);
	});
}

/** Fire a best-effort Redis write: a synchronous throw (client not configured) or a rejection is logged, never propagated. */
export function fireAndForget(label: string, fn: () => PromiseLike<unknown>): void {
	try {
		void Promise.resolve(fn()).catch((error: any) => {
			console.error(`[router] ${label} failed (ignored):`, error?.message || error);
		});
	} catch (error: any) {
		console.error(`[router] ${label} failed (ignored):`, error?.message || error);
	}
}

/** Run a task past the response when the host offers waitUntil (Vercel request context); otherwise best effort. */
export function runInBackground(task: Promise<unknown>): void {
	task.catch(() => {});
	// Vercel's request context, then Next's built-in one (same lookup as shared-libs/logsnag.ts)
	for (const sym of [Symbol.for('@vercel/request-context'), Symbol.for('@next/request-context')]) {
		try {
			const waitUntil = (globalThis as any)[sym]?.get?.()?.waitUntil;
			if (typeof waitUntil === 'function') {
				waitUntil(task);
				return;
			}
		} catch { /* try the next context */ }
	}
}

const OFF = new Set(['false', '0', 'off', 'no']);
const ON = new Set(['true', '1', 'on', 'yes']);

function flagValue(raw: string | undefined, fallback: boolean): boolean {
	const v = (raw ?? '').trim().toLowerCase();
	if (OFF.has(v)) return false;
	if (ON.has(v)) return true;
	return fallback;
}

/** Master switch. Default ON; off = today's behaviour everywhere. */
export function isRouterEnabled(): boolean {
	// Rollout scope: ON by default only for the sibling app and Outfame; every other app that bundles this module
	// (keesy, okgrow) stays on the old rotation unless SOCIAL_ROUTER_ENABLED=true is set explicitly.
	const app = process.env.APP_NAME;
	return flagValue(process.env.SOCIAL_ROUTER_ENABLED, app === 'up' + 'grow' || app === 'outfame');
}

// Static literal reads, one case per known provider id (see the edge-inlining note above).
function providerFlagRaw(id: string): string | undefined {
	switch (id) {
		case 'kicksta': return process.env.SOCIAL_PROV_KICKSTA_ENABLED;
		case 'buzzoid': return process.env.SOCIAL_PROV_BUZZOID_ENABLED;
		case 'upleap': return process.env.SOCIAL_PROV_UPLEAP_ENABLED;
		case 'pathsocial': return process.env.SOCIAL_PROV_PATHSOCIAL_ENABLED;
		case 'igdirect': return process.env.SOCIAL_PROV_IGDIRECT_ENABLED;
		case 'flash': return process.env.SOCIAL_PROV_FLASH_ENABLED;
		case 'starapi': return process.env.SOCIAL_PROV_STARAPI_ENABLED;
		case 'tt_bestexperience': return process.env.SOCIAL_PROV_TT_BESTEXPERIENCE_ENABLED;
		case 'tt_liveapi': return process.env.SOCIAL_PROV_TT_LIVEAPI_ENABLED;
		case 'tt_scraper7': return process.env.SOCIAL_PROV_TT_SCRAPER7_ENABLED;
		case 'highsocial_tt': return process.env.SOCIAL_PROV_HIGHSOCIAL_TT_ENABLED;
		case 'tiktok_oembed': return process.env.SOCIAL_PROV_TIKTOK_OEMBED_ENABLED;
		default: return undefined;
	}
}

export function getCredentials(id: string): { email: string; password: string } | null {
	let email: string | undefined;
	let password: string | undefined;
	switch (id) {
		case 'kicksta':
			email = process.env.SOCIAL_PROV_KICKSTA_EMAIL;
			password = process.env.SOCIAL_PROV_KICKSTA_PASSWORD;
			break;
		case 'buzzoid':
			email = process.env.SOCIAL_PROV_BUZZOID_EMAIL;
			password = process.env.SOCIAL_PROV_BUZZOID_PASSWORD;
			break;
		case 'upleap':
			// the legacy UpleapAPI strategy's login env works too, so the lane needs no new secret
			email = process.env.SOCIAL_PROV_UPLEAP_EMAIL || process.env.UPLEAP_LOGIN_EMAIL;
			password = process.env.SOCIAL_PROV_UPLEAP_PASSWORD || process.env.UPLEAP_LOGIN_PASSWORD;
			break;
		default:
			return null;
	}
	return email && password ? { email, password } : null;
}

const providers = new Map<string, Provider>();

export function registerProvider(provider: Provider): void {
	providers.set(provider.id, provider);
}

export function getProvider(id: string): Provider | undefined {
	return providers.get(id);
}

export function isProviderEnabled(provider: Provider): boolean {
	return flagValue(providerFlagRaw(provider.id), provider.defaultEnabled !== false);
}

/** Registered, flag-enabled and (for Bearer lanes) credentialed providers for a platform+capability. */
export function listProviders(platform: Platform, capability: Capability): Provider[] {
	const out: Provider[] = [];
	for (const p of providers.values()) {
		if (p.platform !== platform || !p.capabilities.includes(capability)) continue;
		if (!isProviderEnabled(p)) continue;
		if (p.requiresToken && !getCredentials(p.id)) continue;
		out.push(p);
	}
	return out;
}

/** Test helper: forget every registration. */
export function __clearProviders(): void {
	providers.clear();
}
